import React from "react";
import { ChevronDown } from "lucide-react";

/**
 * Styled native <select> wrapper.
 *
 * Why native: shadcn/Radix Select uses portals and pointer-event capture which
 * can fail silently on certain mobile browsers and some desktop edge cases.
 * Native <select> is rock-solid across every device/browser.
 *
 * ⚠️ IMPORTANT — API differs from a raw <select>:
 * ``onChange`` receives the SELECTED STRING VALUE, not a synthetic event.
 * Do NOT write ``onChange={(e) => setX(e.target.value)}`` — ``e`` will be
 * a plain string like ``"published"`` and ``e.target.value`` will throw.
 *
 * Correct patterns:
 *   <NativeSelect value={x} onChange={setX} />
 *   <NativeSelect value={x} onChange={(v) => setX(v)} />
 *   <NativeSelect value={x} onChange={(v) => setForm({...form, k: v})} />
 *
 * Options can come from children (native <option> tags) or from a prop:
 *   <NativeSelect value={x} onChange={setX} options={["A","B","C"]} />
 *   <NativeSelect value={x} onChange={setX} options={[{value:"a",label:"A"}]} />
 */
export default function NativeSelect({
  value,
  onChange,
  options,
  placeholder,
  className = "",
  children,
  ...rest
}) {
  const baseCls =
    "w-full h-10 rounded-xl border border-[rgba(17,24,39,0.14)] bg-white px-3 pr-9 text-sm text-[#111827] " +
    "appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C9A86A] focus:border-transparent";

  return (
    <div className={`relative ${className}`}>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={baseCls}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options && options.map((o) => {
          if (o && typeof o === "object" && "value" in o) {
            return <option key={o.value} value={o.value}>{o.label ?? o.value}</option>;
          }
          return <option key={o} value={o}>{o}</option>;
        })}
        {children}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] pointer-events-none" />
    </div>
  );
}
